import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { WonModal, LostModal } from './OutcomeModals'
import BidHealth from './BidHealth'
import { canDeleteOpportunity } from '../permissions'
import Qualification from './Qualification'

const SECTORS = [
  'Central Government',
  'Local Government',
  'Defence',
  'NHS & Health',
  'Education',
  'Police & Emergency Services',
  'Housing',
  'Transport',
  'Utilities',
  'Third Sector',
  'Commercial'
]

const STAGE_CONFIG = {
  identified:   { label: 'Identified',    colour: '4A5568', bg: 'F7FAFC' },
  qualifying:   { label: 'Qualifying',    colour: '2D6A4F', bg: 'EEF7F2' },
  pursuing:     { label: 'Pursuing',      colour: '1A4035', bg: 'D4EAE0' },
  bid_planning: { label: 'Bid Planning',  colour: 'C9A84C', bg: 'FFF8E7' },
  submitted:    { label: 'Submitted',     colour: '2B6CB0', bg: 'EBF8FF' },
  won:          { label: 'Won',           colour: '276749', bg: 'F0FFF4' },
  lost:         { label: 'Lost',          colour: 'C0392B', bg: 'FFF5F5' },
  no_bid:       { label: 'No Bid',        colour: '718096', bg: 'F7FAFC' },
}

const REC_CONFIG = {
  pursue:      { label: 'PURSUE',      bg: '2D6A4F', text: 'FFFFFF' },
  conditional: { label: 'CONDITIONAL', bg: 'C9A84C', text: 'FFFFFF' },
  no_bid:      { label: 'NO BID',      bg: 'C0392B', text: 'FFFFFF' },
}

function formatDate(date) {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric'
  })
}

function getDaysUntil(date) {
  if (!date) return null
  return Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24))
}

function formatValue(min, max) {
  if (!min && !max) return '—'
  const fmt = (v) => {
    if (v >= 1000000) return `£${(v / 1000000).toFixed(1)}m`
    if (v >= 1000) return `£${(v / 1000).toFixed(0)}k`
    return `£${v}`
  }
  if (min && max) return `${fmt(min)} – ${fmt(max)}`
  return fmt(min || max)
}

// ── Quick add form ─────────────────────────────────────────────────────────
function QuickAddForm({ session, profile, orgUsers, frameworks, onSaved, onCancel }) {
  const [form, setForm] = useState({
    title: '', buyer: '', contract_value_min: '',
    contract_value_max: '', deadline: '', framework: '',
    description: '', sector: '', salesforce_url: '',
    bid_lead_id: session.user.id
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleSave = async () => {
    if (!form.title.trim() || !form.buyer.trim()) return
    setSaving(true)
    setError(null)
    const { data, error } = await supabase
      .from('opportunities')
      .insert([{
        title: form.title.trim(),
        buyer: form.buyer.trim(),
        buyer_organisation: form.buyer.trim(),
        contract_value_min: form.contract_value_min || null,
        contract_value_max: form.contract_value_max || null,
        deadline: form.deadline || null,
        framework: form.framework || null,
        description: form.description || null,
        sector: form.sector || null,
        salesforce_url: form.salesforce_url || null,
        organisation_id: profile?.organisation_id || null,
        bid_lead_id: form.bid_lead_id || session.user.id,
        framework_id: form.framework_id || null,
        stage: 'identified',
        status: 'identified',
        source: 'manual',
        created_by: session.user.id
      }])
      .select()
      .single()
    if (error) { setError(error.message); setSaving(false); return }

    // Notify bid lead if assigned and not the creator
    if (data.bid_lead_id && data.bid_lead_id !== session.user.id) {
      await supabase
        .from('notifications')
        .insert([{
          user_id: data.bid_lead_id,
          organisation_id: data.organisation_id,
          opportunity_id: data.id,
          type: 'bid_assigned',
          message: `You have been assigned as Bid Lead for ${data.title}`,
          read: false
        }])
    }

    onSaved(data)
  }

  return (
    <div style={{
      background: 'var(--white)', border: '1px solid var(--green-light)',
      padding: '28px', marginBottom: '24px'
    }}>
      <div style={{
        fontFamily: 'Cormorant Garamond, serif', fontSize: '1.375rem',
        fontWeight: 600, color: 'var(--green-dark)', marginBottom: '20px'
      }}>
        Add New Opportunity
      </div>

      {error && <div className="error-msg">{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>Opportunity Title *</label>
          <input
            type="text" value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. HMRC Digital Transformation — Lot 2"
            autoFocus
          />
        </div>
        <div className="form-group">
          <label>Buyer / Client Organisation *</label>
          <input
            type="text" value={form.buyer}
            onChange={e => setForm({ ...form, buyer: e.target.value })}
            placeholder="e.g. HM Revenue & Customs"
          />
        </div>
        <div className="form-group">
          <label>Framework</label>
          <select
            value={form.framework_id || ''}
            onChange={e => setForm({ ...form, framework_id: e.target.value || null })}
            style={{ width: '100%' }}
          >
            <option value="">N/A</option>
            {(frameworks || []).map(fw => (
              <option key={fw.id} value={fw.id}>{fw.reference_code ? fw.reference_code + ' — ' + fw.name : fw.name}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Estimated Value Min (£)</label>
          <input
            type="number" value={form.contract_value_min}
            onChange={e => setForm({ ...form, contract_value_min: e.target.value })}
            placeholder="e.g. 5000000"
          />
        </div>
        <div className="form-group">
          <label>Estimated Value Max (£)</label>
          <input
            type="number" value={form.contract_value_max}
            onChange={e => setForm({ ...form, contract_value_max: e.target.value })}
            placeholder="e.g. 10000000"
          />
        </div>
        <div className="form-group">
          <label>Submission Deadline</label>
          <input
            type="date" value={form.deadline}
            onChange={e => setForm({ ...form, deadline: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Sector / Industry</label>
          <select
            value={form.sector}
            onChange={e => setForm({ ...form, sector: e.target.value })}
            style={{ width: '100%' }}
          >
            <option value="">Select sector (optional)</option>
            {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Description (optional)</label>
          <input
            type="text" value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder="Brief summary of the opportunity"
          />
        </div>
        <div className="form-group">
          <label>Bid Lead</label>
          <select
            value={form.bid_lead_id}
            onChange={e => setForm({ ...form, bid_lead_id: e.target.value })}
            style={{ width: '100%' }}
          >
            {orgUsers.map(u => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.username}
                {u.id === session.user.id ? ' (you)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Salesforce Opportunity URL (optional)</label>
          <input
            type="text" value={form.salesforce_url}
            onChange={e => setForm({ ...form, salesforce_url: e.target.value })}
            placeholder="https://[org].lightning.force.com/lightning/r/Opportunity/[ID]/view"
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
        <button
          className="btn btn--primary"
          onClick={handleSave}
          disabled={saving || !form.title.trim() || !form.buyer.trim()}
        >
          {saving ? 'Saving...' : 'Add to Pipeline'}
        </button>
        <button className="btn btn--secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

// ── Opportunity card ───────────────────────────────────────────────────────
function OpportunityCard({ opp, qualScore, existingPlan, questionStats, orgUsers, frameworks, profile, onOpenFramework, onOpenCompetitors, onOpenTcAnalysis, onQualify, onBidPlan, onArtefacts, onActions, onQuestions, onCqs, onStageChange, onDelete, onUpdate }) {
  const [editing, setEditing] = useState(false)
  const [showOutcome, setShowOutcome] = useState(false)
  const [showBidHealth, setShowBidHealth] = useState(false)
  const [editForm, setEditForm] = useState({
    title: opp.title || '',
    buyer: opp.buyer || '',
    contract_value_min: opp.contract_value_min || '',
    contract_value_max: opp.contract_value_max || '',
    deadline: opp.deadline ? new Date(opp.deadline).toISOString().split('T')[0] : '',
    framework: opp.framework || '',
    description: opp.description || '',
    sector: opp.sector || '',
    salesforce_url: opp.salesforce_url || '',
    bid_lead_id: opp.bid_lead_id || '',
    framework_id: opp.framework_id || '',
    tc_url: opp.tc_url || ''
  })
  const [saving, setSaving] = useState(false)

  const handleUpdate = async () => {
    setSaving(true)
    const { data, error } = await supabase
      .from('opportunities')
      .update({
        title: editForm.title.trim(),
        buyer: editForm.buyer.trim(),
        buyer_organisation: editForm.buyer.trim(),
        contract_value_min: editForm.contract_value_min || null,
        contract_value_max: editForm.contract_value_max || null,
        deadline: editForm.deadline || null,
        framework: editForm.framework || null,
        description: editForm.description || null,
        sector: editForm.sector || null,
        salesforce_url: editForm.salesforce_url || null,
        bid_lead_id: editForm.bid_lead_id || null,
        framework_id: editForm.framework_id || null,
        tc_url: editForm.tc_url || null,
      })
      .eq('id', opp.id)
      .select()
      .single()
    setSaving(false)
    if (!error && data) {
      // If bid lead changed create a notification
      if (editForm.bid_lead_id && editForm.bid_lead_id !== (opp.bid_lead_id || '')) {
        await supabase
          .from('notifications')
          .insert([{
            user_id: editForm.bid_lead_id,
            organisation_id: data.organisation_id,
            opportunity_id: data.id,
            type: 'bid_assigned',
            message: `You have been assigned as Bid Lead for ${data.title}`,
            read: false
          }])
      }
      onUpdate(data)
      setEditing(false)
    }
  }
    const daysUntil = getDaysUntil(opp.deadline)
  const stage = STAGE_CONFIG[opp.stage] || STAGE_CONFIG.identified
  const rec = qualScore ? REC_CONFIG[qualScore.recommendation] : null

  return (
    <div style={{
      background: 'var(--white)', border: '1px solid var(--green-light)',
      marginBottom: '12px', transition: 'box-shadow 0.2s',
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(45,106,79,0.1)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      {/* Card body */}
    {/* Card body */}
      <div style={{ padding: '20px 24px' }}>
        {editing ? (
          // ── Edit mode ──
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Opportunity Title *</label>
                <input
                  type="text" value={editForm.title}
                  onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Buyer / Client Organisation *</label>
                <input
                  type="text" value={editForm.buyer}
                  onChange={e => setEditForm({ ...editForm, buyer: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Sector</label>
                <select
                  value={editForm.sector}
                  onChange={e => setEditForm({ ...editForm, sector: e.target.value })}
                  style={{ width: '100%' }}
                >
                  <option value="">Select sector</option>
                  {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Framework</label>
                <select
                  value={editForm.framework_id || ''}
                  onChange={e => setEditForm({ ...editForm, framework_id: e.target.value || null })}
                  style={{ width: '100%' }}
                >
                  <option value="">N/A</option>
                  {(frameworks || []).map(fw => (
                    <option key={fw.id} value={fw.id}>{fw.reference_code ? fw.reference_code + ' — ' + fw.name : fw.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Opportunity T&amp;C URL (optional)</label>
                <input
                  type="text"
                  value={editForm.tc_url}
                  onChange={e => setEditForm({ ...editForm, tc_url: e.target.value })}
                  placeholder="https://... link to buyer's specific T&C document"
                />
              </div>
              <div className="form-group">
                <label>Value Min (£)</label>
                <input
                  type="number" value={editForm.contract_value_min}
                  onChange={e => setEditForm({ ...editForm, contract_value_min: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Value Max (£)</label>
                <input
                  type="number" value={editForm.contract_value_max}
                  onChange={e => setEditForm({ ...editForm, contract_value_max: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Submission Deadline</label>
                <input
                  type="date" value={editForm.deadline}
                  onChange={e => setEditForm({ ...editForm, deadline: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text" value={editForm.description}
                  onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Bid Lead</label>
                <select
                  value={editForm.bid_lead_id}
                  onChange={e => setEditForm({ ...editForm, bid_lead_id: e.target.value })} 
                  style={{ width: '100%' }}
                >
                  <option value="">Unassigned</option>
                  {orgUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.full_name || u.username}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Salesforce Opportunity URL (optional)</label>
                <input
                  type="text" value={editForm.salesforce_url}
                  onChange={e => setEditForm({ ...editForm, salesforce_url: e.target.value })}
                  placeholder="https://[org].lightning.force.com/lightning/r/Opportunity/[ID]/view"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
              <button
                className="btn btn--primary"
                onClick={handleUpdate}
                disabled={saving || !editForm.title.trim() || !editForm.buyer.trim()}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                className="btn btn--secondary"
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          // ── View mode ──
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: '0.625rem', fontWeight: 700,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  color: `#${stage.colour}`, background: `#${stage.bg}`,
                  padding: '3px 8px', border: `1px solid #${stage.colour}30`
                }}>
                  {stage.label}
                </span>
                {rec && (
                  <span style={{
                    fontSize: '0.625rem', fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: `#${rec.text}`, background: `#${rec.bg}`,
                    padding: '3px 8px'
                  }}>
                    {rec.label}
                  </span>
                )}
                {opp.sector && (
                  <span style={{
                    fontSize: '0.625rem', color: 'var(--white)',
                    background: 'var(--green-mid)', padding: '3px 8px'
                  }}>
                    {opp.sector}
                  </span>
                )}
                 {opp.framework_id && frameworks.length > 0 && (() => {
                  const fw = frameworks.find(f => f.id === opp.framework_id)
                  if (!fw) return null
                  return (
                    <span
                      onClick={e => { e.stopPropagation(); onOpenFramework(opp.framework_id) }}
                      style={{ fontSize: '0.625rem', color: 'var(--slate)', background: 'var(--green-pale)', padding: '3px 8px', cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--green-light)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'var(--green-pale)' }}
                      title="View framework"
                    >
                      {fw.reference_code || fw.name}
                    </span>
                  )
                })()}
              </div>
              <div style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: '1.25rem', fontWeight: 600,
                color: 'var(--green-dark)', marginBottom: '4px',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
              }}>
                {opp.title}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--slate)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <span>{opp.buyer}</span>
                {opp.bid_lead_id && orgUsers.length > 0 && (
                  <span style={{
                    fontSize: '0.75rem', fontWeight: 600,
                    color: 'var(--green-dark)', background: 'var(--green-pale)',
                    padding: '2px 8px'
                  }}>
                    BL: {orgUsers.find(u => u.id === opp.bid_lead_id)?.full_name ||
                         orgUsers.find(u => u.id === opp.bid_lead_id)?.username ||
                         'Unknown'}
                  </span>
                )}
                {opp.salesforce_url && (
                  <span
                    onClick={e => { e.stopPropagation(); window.open(opp.salesforce_url, '_blank') }}
                    style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--white)', background: '#009EDB', padding: '2px 8px', cursor: 'pointer', letterSpacing: '0.04em', flexShrink: 0 }}
                  >
                    ⚡ Salesforce
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '2px' }}>Value</div>
                  <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--charcoal)' }}>
                    {formatValue(opp.contract_value_min, opp.contract_value_max)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '2px' }}>Deadline</div>
                  <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: daysUntil !== null && daysUntil < 14 ? 'var(--red)' : 'var(--charcoal)' }}>
                    {formatDate(opp.deadline)}
                  </div>
                </div>
                {daysUntil !== null && (
                  <div>
                    <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '2px' }}>Remaining</div>
                    <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: daysUntil < 7 ? 'var(--red)' : daysUntil < 14 ? 'var(--gold-dark)' : 'var(--charcoal)' }}>
                      {daysUntil < 0 ? 'Past deadline' : daysUntil === 0 ? 'Due today' : `${daysUntil} days`}
                    </div>
                  </div>
                )}
                {qualScore && (
                  <div>
                    <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '2px' }}>Qual Score</div>
                    <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--charcoal)' }}>
                      {parseFloat(qualScore.overall_score).toFixed(2)} / 5.00
                    </div>
                  </div>
                )}
                {qualScore?.pwin_adjusted && (
                  <div>
                    <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '2px' }}>PWin</div>
                    <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--charcoal)' }}>
                      {qualScore.pwin_adjusted}%
                    </div>
                  </div>
                )}
                {questionStats && (
                <div>
                  <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '2px' }}>Response</div>
                  <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: questionStats.complete === questionStats.total ? 'var(--green)' : 'var(--charcoal)' }}>
                    {questionStats.complete}/{questionStats.total} questions
                  </div>
                </div>
              )}
              </div>
            </div>
            <div style={{ flexShrink: 0 }}>
              <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '6px' }}>Stage</div>
              <select
                value={opp.stage}
                onChange={e => onStageChange(opp.id, e.target.value)}
                style={{
                  padding: '6px 10px',
                  border: `1px solid #${stage.colour}`,
                  fontSize: '0.8125rem', fontWeight: 600,
                  color: `#${stage.colour}`,
                  background: `#${stage.bg}`,
                  borderRadius: 0, outline: 'none', cursor: 'pointer'
                }}
              >
                {Object.entries(STAGE_CONFIG).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Action bar */}
      <div style={{
        borderTop: '1px solid var(--green-light)',
        background: 'var(--green-pale)',
        padding: '8px 16px',
        display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center'
      }}>
        {/* Qualify / View Score */}
        <button
          onClick={() => onQualify(opp)}
          style={{
            background: qualScore ? 'var(--green)' : 'none',
            border: `1px solid ${qualScore ? 'var(--green)' : 'var(--green)'}`,
            color: qualScore ? 'var(--white)' : 'var(--green)',
            padding: '5px 14px', fontSize: '0.75rem', fontWeight: 600,
            letterSpacing: '0.04em', cursor: 'pointer', transition: 'all 0.15s'
          }}
        >
          {qualScore ? '✓ View Scorecard' : '+ Qualify'}
        </button>

        {/* Bid Plan */}
        <button
          onClick={() => onBidPlan(opp, existingPlan?.id || null)}
          style={{
            background: existingPlan ? 'var(--green)' : 'none',
            border: '1px solid var(--green)',
            color: existingPlan ? 'var(--white)' : 'var(--green)',
            padding: '5px 14px', fontSize: '0.75rem', fontWeight: 600,
            letterSpacing: '0.04em', cursor: 'pointer', transition: 'all 0.15s'
          }}
        >
          {existingPlan ? '▶ Bid Plan' : '+ Bid Plan'}
        </button>

        {/* Coming soon modules */}
        <button
          onClick={() => onArtefacts(opp)}
          style={{
            background: 'none', border: '1px solid var(--green)',
            color: 'var(--green)', padding: '5px 14px',
            fontSize: '0.75rem', fontWeight: 600,
            letterSpacing: '0.04em', cursor: 'pointer', transition: 'all 0.15s'
          }}
        >
          Artefacts
        </button>
<button
          onClick={() => onActions(opp)}
          style={{
            background: 'none', border: '1px solid var(--green)',
            color: 'var(--green)', padding: '5px 14px',
            fontSize: '0.75rem', fontWeight: 600,
            letterSpacing: '0.04em', cursor: 'pointer', transition: 'all 0.15s'
          }}
        >
          Actions
        </button>

        <button
          onClick={() => onQuestions(opp)}
          style={{
            background: 'none', border: '1px solid var(--green)',
            color: 'var(--green)', padding: '5px 14px',
            fontSize: '0.75rem', fontWeight: 600,
            letterSpacing: '0.04em', cursor: 'pointer', transition: 'all 0.15s'
          }}
        >
          Response Questions
        </button>

<button
          onClick={() => onCqs(opp)}
          style={{
            background: 'none', border: '1px solid var(--green)',
            color: 'var(--green)', padding: '5px 14px',
            fontSize: '0.75rem', fontWeight: 600,
            letterSpacing: '0.04em', cursor: 'pointer', transition: 'all 0.15s'
          }}
        >
          CQs
        </button>
        <button
          onClick={() => onOpenCompetitors && onOpenCompetitors(opp)}
          style={{
            background: 'none', border: '1px solid var(--green)',
            color: 'var(--green)', padding: '5px 14px',
            fontSize: '0.75rem', fontWeight: 600,
            letterSpacing: '0.04em', cursor: 'pointer', transition: 'all 0.15s'
          }}
        >
          Competitors
        </button>
        {profile?.organisations?.ai_enabled && existingPlan && (
          <button
            onClick={() => setShowBidHealth(true)}
            style={{
              background: opp.bid_health ? (opp.bid_health.status === 'Critical' ? '#FFF5F5' : opp.bid_health.status === 'At Risk' ? '#FEF3E2' : 'var(--green-pale)') : 'none',
              border: '1px solid ' + (opp.bid_health ? (opp.bid_health.status === 'Critical' ? 'var(--red)' : opp.bid_health.status === 'At Risk' ? '#E67E22' : 'var(--green)') : 'var(--green)'),
              color: opp.bid_health ? (opp.bid_health.status === 'Critical' ? 'var(--red)' : opp.bid_health.status === 'At Risk' ? '#E67E22' : 'var(--green-dark)') : 'var(--green)',
              padding: '5px 14px', fontSize: '0.75rem', fontWeight: 600,
              letterSpacing: '0.04em', cursor: 'pointer', transition: 'all 0.15s'
            }}
          >
            {opp.bid_health ? '● ' + opp.bid_health.status : '✦ Bid Health'}
          </button>
        )}
        {opp.framework_id && opp.tc_url && (
          <button
            onClick={() => onOpenTcAnalysis && onOpenTcAnalysis(opp)}
            style={{
              background: 'none', border: '1px solid var(--green-dark)',
              color: 'var(--green-dark)', padding: '5px 14px',
              fontSize: '0.75rem', fontWeight: 600,
              letterSpacing: '0.04em', cursor: 'pointer', transition: 'all 0.15s'
            }}
          >
            T&amp;C Variance
          </button>
        )}
        {(opp.stage === 'won' || opp.stage === 'lost') && opp.outcome_detail && (
          <button
            onClick={() => setShowOutcome(prev => !prev)}
            style={{
              background: opp.stage === 'won' ? 'var(--green-pale)' : '#FFF5F5',
              border: '1px solid ' + (opp.stage === 'won' ? 'var(--green)' : '#C0392B'),
              color: opp.stage === 'won' ? 'var(--green-dark)' : '#C0392B',
              padding: '5px 14px', fontSize: '0.75rem', fontWeight: 600,
              letterSpacing: '0.04em', cursor: 'pointer', transition: 'all 0.15s'
            }}
          >
            {showOutcome ? 'Hide Outcome' : 'Outcome Detail'}
          </button>
        )}
{/* Edit */}
        <button
          onClick={() => setEditing(true)}
          style={{
            background: 'none',
            border: '1px solid var(--green)',
            color: 'var(--green)',
            padding: '5px 14px', fontSize: '0.75rem', fontWeight: 600,
            letterSpacing: '0.04em', cursor: 'pointer', transition: 'all 0.15s',
            marginLeft: 'auto'
          }}
        >
          Edit
        </button>


        {showBidHealth && (
        <BidHealth
          opportunity={opp}
          profile={profile}
          onClose={() => setShowBidHealth(false)}
          onHealthSaved={(health) => { onUpdate && onUpdate({ ...opp, bid_health: health }); setShowBidHealth(false) }}
        />
      )}
      {/* Outcome detail panel */}
      {showOutcome && opp.outcome_detail && (
        <div style={{ margin: '12px 0 0', padding: '16px 20px', background: opp.stage === 'won' ? 'var(--green-pale)' : '#FFF5F5', borderTop: '1px solid ' + (opp.stage === 'won' ? 'var(--green-light)' : '#FCCACA') }}>
          <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: opp.stage === 'won' ? 'var(--green-dark)' : '#C0392B', marginBottom: '12px' }}>
            {opp.stage === 'won' ? '🏆 Win Close-Out' : '📋 Loss Close-Out'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '12px' }}>
            {opp.outcome_detail.contract_value && (
              <div>
                <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Contract Value</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--green-dark)' }}>£{opp.outcome_detail.contract_value.toLocaleString()}</div>
              </div>
            )}
            {opp.outcome_detail.start_date && (
              <div>
                <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Start Date</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)' }}>{new Date(opp.outcome_detail.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
              </div>
            )}
            {opp.outcome_detail.duration_months && (
              <div>
                <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Duration</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)' }}>{opp.outcome_detail.duration_months} months</div>
              </div>
            )}
            {opp.outcome_detail.lost_to && (
              <div>
                <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Lost To</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#C0392B' }}>{opp.outcome_detail.lost_to}</div>
              </div>
            )}
            {opp.outcome_detail.lost_stage && (
              <div>
                <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Lost at Stage</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)' }}>{opp.outcome_detail.lost_stage}</div>
              </div>
            )}
            {opp.outcome_detail.primary_reason && (
              <div>
                <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Primary Reason</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)' }}>{opp.outcome_detail.primary_reason}</div>
              </div>
            )}
            {opp.outcome_detail.technical_score && (
              <div>
                <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Technical Score</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--green-dark)' }}>{opp.outcome_detail.technical_score}%</div>
              </div>
            )}
            {opp.outcome_detail.commercial_score && (
              <div>
                <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Commercial Score</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--green-dark)' }}>{opp.outcome_detail.commercial_score}%</div>
              </div>
            )}
            {opp.outcome_detail.bid_again && (
              <div>
                <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Bid Again?</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)' }}>{opp.outcome_detail.bid_again}</div>
              </div>
            )}
          </div>
          {opp.outcome_detail.win_factors && (
            <div style={{ marginBottom: '8px' }}>
              <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Win Factors</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--charcoal)', lineHeight: 1.5 }}>{opp.outcome_detail.win_factors}</div>
            </div>
          )}
          {opp.outcome_detail.lessons_learned && (
            <div>
              <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Lessons Learned</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--charcoal)', lineHeight: 1.5 }}>{opp.outcome_detail.lessons_learned}</div>
            </div>
          )}
        </div>
      )}
      {/* Delete — pushed right */}
       {canDeleteOpportunity(profile, opp) && <button
          onClick={() => onDelete(opp.id)}
          style={{
            background: 'none', border: '1px solid var(--silver)',
            color: 'var(--slate)', padding: '5px 10px',
            fontSize: '0.75rem', cursor: 'pointer',
            transition: 'all 0.15s'
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
        >
          Delete
        </button>}
      </div>
    </div>
  )
}

// ── Main Opportunities component ───────────────────────────────────────────
function Opportunities({ session, profile, orgUsers, onNavigate, onOpenBidPlan, onOpenArtefacts, onOpenActions, onOpenQuestions, onOpenCqs, onOpenFramework, onOpenCompetitors, onOpenTcAnalysis, initialFilter }) {
  const [opportunities, setOpportunities] = useState([])
  const [qualScores, setQualScores] = useState({})
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [qualifyingOpp, setQualifyingOpp] = useState(null)
  const [wonOpp, setWonOpp] = useState(null)
  const [lostOpp, setLostOpp] = useState(null)
  const [competitors, setCompetitors] = useState([])
  const [filterStage, setFilterStage] = useState(initialFilter === 'won_lost' ? 'won' : 'all')
  const [showDecidedOnly] = useState(initialFilter === 'won_lost')
  const [filterSector, setFilterSector] = useState('all')
  const [filterPeriod, setFilterPeriod] = useState('all')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')
  const [bidPlanMap, setBidPlanMap] = useState({})
  const [questionMap, setQuestionMap] = useState({})
  const [frameworks, setFrameworks] = useState([])

  useEffect(() => {
    if (profile?.organisation_id) {
      loadOpportunities()
      
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organisation_id])

    useEffect(() => {
    if (profile?.organisation_id) {
      supabase.from('competitors').select('id, name').eq('organisation_id', profile.organisation_id).order('name').then(({ data }) => setCompetitors(data || []))
    }
  }, [profile?.organisation_id])

  const loadOpportunities = async () => {
    setLoading(true)
    const { data: opps } = await supabase
      .from('opportunities')
      .select('*, bid_health')
      .eq('organisation_id', profile?.organisation_id)
      .order('created_at', { ascending: false })

    if (opps && opps.length > 0) {
      const oppIds = opps.map(o => o.id)

      const { data: scores } = await supabase
        .from('qualification_scores')
        .select('*')
        .in('opportunity_id', oppIds)

      const scoreMap = {}
      if (scores) scores.forEach(s => { scoreMap[s.opportunity_id] = s })
      setQualScores(scoreMap)

      const { data: plans } = await supabase
        .from('bid_plans')
        .select('id, opportunity_id, status')
        .in('opportunity_id', oppIds)

      const planMap = {}
      if (plans) plans.forEach(p => { planMap[p.opportunity_id] = p })
      setBidPlanMap(planMap)

      // Fetch question completion counts
      const { data: questionStats } = await supabase
        .from('questions')
        .select('opportunity_id, status')
        .in('opportunity_id', oppIds)

      const questionMap = {}
      if (questionStats) {
        oppIds.forEach(id => {
          const oppQuestions = questionStats.filter(q => q.opportunity_id === id)
          if (oppQuestions.length > 0) {
            questionMap[id] = {
              total: oppQuestions.length,
              complete: oppQuestions.filter(q => q.status === 'complete').length
            }
          }
        })
      }
      setQuestionMap(questionMap)
    }

    
    // Load frameworks for dropdown
    if (profile?.organisation_id) {
      const { data: fws } = await supabase
        .from('frameworks')
        .select('id, name, reference_code')
        .eq('organisation_id', profile.organisation_id)
        .eq('is_active', true)
        .order('name')
      setFrameworks(fws || [])
    }

    setOpportunities(opps || [])
    setLoading(false)
  }

  const handleSaved = (newOpp) => {
    setOpportunities(prev => [newOpp, ...prev])
    setShowAdd(false)
  }

  const handleStageChange = async (oppId, newStage) => {
    if (newStage === 'won' || newStage === 'lost') {
      const opp = opportunities.find(o => o.id === oppId)
      if (opp) {
        if (newStage === 'won') setWonOpp(opp)
        else setLostOpp(opp)
        return
      }
    }
    await supabase.from('opportunities').update({ stage: newStage }).eq('id', oppId)
    setOpportunities(prev => prev.map(o => o.id === oppId ? { ...o, stage: newStage } : o))
  }

  const handleDelete = async (oppId) => {
    await supabase.from('opportunities').delete().eq('id', oppId)
    setOpportunities(prev => prev.filter(o => o.id !== oppId))
  }

  const handleQualScoreSaved = (oppId, score) => {
    setQualScores(prev => ({ ...prev, [oppId]: score }))
    setOpportunities(prev => prev.map(o =>
      o.id === oppId ? { ...o, stage: 'pursuing', status: 'pursuing' } : o
    ))
    setQualifyingOpp(null)
  }

  // Filter
  const getDateRange = () => {
    const now = new Date()
    if (filterPeriod === 'month') return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: new Date(now.getFullYear(), now.getMonth() + 1, 0) }
    if (filterPeriod === 'quarter') { const q = Math.floor(now.getMonth() / 3); return { from: new Date(now.getFullYear(), q * 3, 1), to: new Date(now.getFullYear(), q * 3 + 3, 0) } }
    if (filterPeriod === 'year') return { from: new Date(now.getFullYear(), 0, 1), to: new Date(now.getFullYear(), 11, 31) }
    if (filterPeriod === 'custom' && filterDateFrom && filterDateTo) return { from: new Date(filterDateFrom), to: new Date(filterDateTo) }
    return null
  }
  const dateRange = getDateRange()


  const decidedOpps = opportunities.filter(o =>
    (o.stage === 'won' || o.stage === 'lost') &&
    (filterSector === 'all' || o.sector === filterSector)
  )
  const wonCount = decidedOpps.filter(o => o.stage === 'won').length
  const winRatePct = decidedOpps.length > 0 ? Math.round((wonCount / decidedOpps.length) * 100) : null
  const winRateColour = winRatePct === null ? '#C9A84C' : winRatePct >= 50 ? 'var(--green)' : winRatePct >= 25 ? '#E67E22' : 'var(--red)'

  const filtered = opportunities
    .filter(o => showDecidedOnly ? (o.stage === 'won' || o.stage === 'lost') : (filterStage === 'all' || o.stage === filterStage))
    .filter(o => filterSector === 'all' || o.sector === filterSector)
    .filter(o => { if (!dateRange) return true; const created = new Date(o.created_at); return created >= dateRange.from && created <= dateRange.to })

  // If qualifying an opportunity, show the qualification module
  if (qualifyingOpp) {
    return (
      <Qualification
        session={session}
        profile={profile}
        onNavigate={() => setQualifyingOpp(null)}
        preloadedOpportunity={qualifyingOpp}
        existingScore={qualScores[qualifyingOpp.id] || null}
        onScoreSaved={(score) => handleQualScoreSaved(qualifyingOpp.id, score)}
      />
    )
  }

  return (
    <div className="scorecard-page container">
      <div className="page-header">
        <p className="label">Opportunity Pipeline</p>
        <h1>Opportunities</h1>
        <p>Track every opportunity from identification through to outcome. All bid activity links back here.</p>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', gap: '12px' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {['all', ...Object.keys(STAGE_CONFIG)].map(stage => {
            const count = stage === 'all'
              ? opportunities.length
              : opportunities.filter(o => o.stage === stage).length
            if (stage !== 'all' && count === 0) return null
            return (
              <button
                key={stage}
                onClick={() => setFilterStage(stage)}
                style={{
                  background: filterStage === stage ? 'var(--green)' : 'none',
                  border: `1px solid ${filterStage === stage ? 'var(--green)' : 'var(--silver)'}`,
                  color: filterStage === stage ? 'var(--white)' : 'var(--slate)',
                  padding: '4px 12px', fontSize: '0.75rem', fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.15s'
                }}
              >
                {stage === 'all' ? 'All' : STAGE_CONFIG[stage].label} ({count})
              </button>
            )
          })}
        </div>
        {/* Sector filters — only show sectors that have opportunities */}
        {SECTORS.filter(s => opportunities.some(o => o.sector === s)).length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', width: '100%' }}>
            <button
              onClick={() => setFilterSector('all')}
              style={{
                background: filterSector === 'all' ? 'var(--green-mid)' : 'none',
                border: `1px solid ${filterSector === 'all' ? 'var(--green-mid)' : 'var(--silver)'}`,
                color: filterSector === 'all' ? 'var(--white)' : 'var(--slate)',
                padding: '4px 12px', fontSize: '0.75rem', fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s'
              }}
            >
              All Sectors
            </button>
            {SECTORS.filter(s => opportunities.some(o => o.sector === s)).map(sector => (
              <button
                key={sector}
                onClick={() => setFilterSector(sector === filterSector ? 'all' : sector)}
                style={{
                  background: filterSector === sector ? 'var(--green-mid)' : 'none',
                  border: `1px solid ${filterSector === sector ? 'var(--green-mid)' : 'var(--silver)'}`,
                  color: filterSector === sector ? 'var(--white)' : 'var(--slate)',
                  padding: '4px 12px', fontSize: '0.75rem', fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.15s'
                }}
              >
                {sector} ({opportunities.filter(o => o.sector === sector).length})
              </button>
            ))}
          </div>
        )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
          {winRatePct !== null && (
            <div style={{ textAlign: 'right', marginBottom: '8px' }}>
              <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--slate)', marginBottom: '2px' }}>
                {filterSector === 'all' ? 'Win Rate' : filterSector}
              </div>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '3rem', fontWeight: 700, color: winRateColour, lineHeight: 1 }}>
                {winRatePct}%
              </div>
            </div>
          )}
          <button
            className="btn btn--primary"
            onClick={() => setShowAdd(true)}
          >
            + Add Opportunity
          </button>
        </div>
      </div>

      {/* Quick add form */}
      {showAdd && (
        <QuickAddForm
          session={session}
          profile={profile}
          orgUsers={orgUsers}
          frameworks={frameworks}
          onSaved={handleSaved}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {loading && <p style={{ color: 'var(--slate)' }}>Loading pipeline...</p>}

      {!loading && opportunities.length === 0 && !showAdd && (
        <div className="card">
          <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '32px 0' }}>
            No opportunities yet. Click <strong>+ Add Opportunity</strong> to add your first one.
          </p>
        </div>
      )}

      {!loading && filtered.length === 0 && opportunities.length > 0 && (
        <div className="card">
          <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '24px 0' }}>
            No opportunities yet at this stage.
          </p>
        </div>
      )}


        {/* Period filter */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', width: '100%', alignItems: 'center' }}>
          {['all', 'month', 'quarter', 'year', 'custom'].map(period => (
            <button
              key={period}
              onClick={() => setFilterPeriod(period)}
              style={{
                background: filterPeriod === period ? 'var(--green-dark)' : 'none',
                border: '1px solid ' + (filterPeriod === period ? 'var(--green-dark)' : 'var(--silver)'),
                color: filterPeriod === period ? 'var(--white)' : 'var(--slate)',
                padding: '4px 12px', fontSize: '0.75rem', fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s'
              }}
            >
              {period === 'all' ? 'All Time' : period === 'month' ? 'This Month' : period === 'quarter' ? 'This Quarter' : period === 'year' ? 'This Year' : 'Custom'}
            </button>
          ))}
          {filterPeriod === 'custom' && (
            <>
              <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} style={{ padding: '4px 8px', border: '1px solid var(--silver)', fontSize: '0.75rem', borderRadius: 0 }} />
              <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>to</span>
              <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} style={{ padding: '4px 8px', border: '1px solid var(--silver)', fontSize: '0.75rem', borderRadius: 0 }} />
            </>
          )}
        </div>

      {filtered.map(opp => (
       <OpportunityCard
          key={opp.id}
          opp={opp}
          qualScore={qualScores[opp.id] || null}
          existingPlan={bidPlanMap[opp.id] || null}
          questionStats={questionMap[opp.id] || null}
          frameworks={frameworks}
          onOpenFramework={onOpenFramework}
          onOpenCompetitors={onOpenCompetitors}
          onOpenTcAnalysis={onOpenTcAnalysis}
          orgUsers={orgUsers}
          profile={profile}
          onQualify={(o) => setQualifyingOpp(o)}
          onBidPlan={(o, planId) => onOpenBidPlan(o, planId)}
          onArtefacts={(o) => onOpenArtefacts(o)}
          onActions={(o) => onOpenActions(o)}
          onQuestions={(o) => onOpenQuestions(o)}
          onCqs={(o) => onOpenCqs(o)}
          onStageChange={handleStageChange}
          onDelete={handleDelete}
          onUpdate={(updated) => setOpportunities(prev =>
            prev.map(o => o.id === updated.id ? updated : o)
          )}
        />
))}
      <div className="actions" style={{ marginTop: '8px' }}>
        <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>
          ← Dashboard
        </button>
      </div>

      {wonOpp && (
        <WonModal
          opportunity={wonOpp}
          competitors={competitors}
          onSave={() => {
            setWonOpp(null)
            setOpportunities(prev => prev.map(o => o.id === wonOpp.id ? { ...o, stage: 'won' } : o))
          }}
          onClose={() => setWonOpp(null)}
        />
      )}

      {lostOpp && (
        <LostModal
          opportunity={lostOpp}
          competitors={competitors}
          onSave={() => {
            setLostOpp(null)
            setOpportunities(prev => prev.map(o => o.id === lostOpp.id ? { ...o, stage: 'lost' } : o))
          }}
          onClose={() => setLostOpp(null)}
        />
      )}
    </div>
  )
}

export default Opportunities